import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { usePermissions } from '../contexts/PermissionContext';
import { DEFAULT_HOME_ROUTE, getHomeScreenRouteFromAppRow } from '../lib/controlCentreHome';
import BasicLoadingScreen from './BasicLoadingScreen';

/**
 * Redirects to the business-configured home screen, or Control Centre by default.
 */
export default function HomeScreenRedirect() {
  const { selectedBusiness } = useBusiness();
  const { permissions, loading: permissionsLoading } = usePermissions();
  const [homeScreenRoute, setHomeScreenRoute] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    determineHomeScreen();
  }, [selectedBusiness, permissionsLoading, permissions?.topLevel?.homeScreenAppId]);

  const determineHomeScreen = async () => {
    if (!selectedBusiness?.id) {
      setLoading(false);
      return;
    }

    if (permissionsLoading) {
      return;
    }

    try {
      const roleHomeScreenAppId = permissions?.topLevel?.homeScreenAppId || null;

      if (roleHomeScreenAppId) {
        const { data: roleHomeScreenApp, error: roleHomeError } = await supabase
          .from('business_apps_v2')
          .select(`
            app_id,
            app:app_registry_v2(
              id,
              slug,
              route_prefix
            )
          `)
          .eq('business_id', selectedBusiness.id)
          .eq('status', 'active')
          .eq('app_id', roleHomeScreenAppId)
          .maybeSingle();

        const roleRoute = getHomeScreenRouteFromAppRow(roleHomeScreenApp);
        if (!roleHomeError && roleRoute) {
          console.log('🏠 Role home screen redirect to:', roleRoute);
          setHomeScreenRoute(roleRoute);
          setLoading(false);
          return;
        }
      }

      const { data: homeScreenApp, error } = await supabase
        .from('business_apps_v2')
        .select(`
          app_id,
          app:app_registry_v2(
            id,
            slug,
            route_prefix
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('is_home_screen', true)
        .maybeSingle();

      const businessRoute = getHomeScreenRouteFromAppRow(homeScreenApp);
      if (!error && businessRoute) {
        console.log('🏠 Home screen redirect to:', businessRoute);
        setHomeScreenRoute(businessRoute);
      } else {
        console.log('No home screen set, redirect to Control Centre');
        setHomeScreenRoute(DEFAULT_HOME_ROUTE);
      }
    } catch (err) {
      console.error('Error determining home screen:', err);
      setHomeScreenRoute(DEFAULT_HOME_ROUTE);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <BasicLoadingScreen />;
  }

  return homeScreenRoute ? (
    <Navigate to={homeScreenRoute} replace />
  ) : (
    <Navigate to={DEFAULT_HOME_ROUTE} replace />
  );
}
